import React, { useState } from 'react';
import { ShieldAlert, Send, CheckCircle2, Loader2, ArrowLeft, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReportPiracyPage = () => {
  const [form, setForm] = useState({
    reporter_name: '', reporter_email: '', offender: '',
    offending_url: '', original_work: '', original_url: '',
    description: '', platform: 'Roblox',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.reporter_email || !form.offending_url || form.description.length < 20) {
      toast.error('Please fill required fields (description ≥ 20 chars)');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/public/dmca/report`, form);
      setDone(res.data);
      toast.success('Report submitted — thank you');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white pt-24 pb-16 px-4 relative overflow-hidden" data-testid="public-report-page">
      {/* Ambient */}
      <div className="pointer-events-none absolute top-0 right-0 w-[600px] h-[400px] bg-[#8A2BE2]/[0.04] rounded-full blur-[160px] -translate-y-1/4 translate-x-1/4" />
      <div className="pointer-events-none absolute bottom-0 left-0 w-[500px] h-[400px] bg-[#00E5FF]/[0.03] rounded-full blur-[160px] translate-y-1/4 -translate-x-1/4" />

      <div className="max-w-2xl mx-auto relative">
        <Link to="/" className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-white/40 hover:text-white/70 mb-6" data-testid="back-home">
          <ArrowLeft className="w-3 h-3" /> Back to home
        </Link>

        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/[0.08] bg-white/[0.02] mb-5">
          <ShieldAlert className="w-3 h-3 text-[#c39be5]" strokeWidth={1.5} />
          <span className="text-[10px] tracking-[0.25em] uppercase text-white/60 font-medium">Compliance · DMCA</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-light tracking-tighter mb-3">
          Report <span className="bg-gradient-to-r from-[#c39be5] to-red-300 bg-clip-text text-transparent font-medium">Copyright Theft</span>
        </h1>
        <p className="text-white/50 text-sm md:text-base tracking-wide leading-relaxed max-w-lg mb-8">
          Seen a game, logo, image or asset copied from Starnight Holdings without permission? Let us know and our DMCA team will investigate. All reports are confidential.
        </p>

        {done ? (
          <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.04] p-8 text-center" data-testid="report-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" strokeWidth={1.5} />
            <h2 className="text-xl font-light tracking-tight mb-2">Report received</h2>
            <p className="text-white/60 text-sm mb-4">{done.message}</p>
            <p className="text-[10px] tracking-[0.15em] uppercase text-white/30">Reference: {done.case_id}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 rounded-2xl border border-white/[0.08] bg-[#0A0A12]/60 backdrop-blur-md p-6 md:p-8">
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Your name *"><input required value={form.reporter_name} onChange={e => setForm(f => ({ ...f, reporter_name: e.target.value }))} className={INPUT} data-testid="rep-name" /></Field>
              <Field label="Your email *"><input required type="email" value={form.reporter_email} onChange={e => setForm(f => ({ ...f, reporter_email: e.target.value }))} className={INPUT} data-testid="rep-email" /></Field>
            </div>
            <Field label="Where is the stolen content? (URL) *">
              <input required value={form.offending_url} onChange={e => setForm(f => ({ ...f, offending_url: e.target.value }))} className={INPUT} placeholder="https://…" data-testid="rep-offending-url" />
            </Field>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Offender name / handle"><input value={form.offender} onChange={e => setForm(f => ({ ...f, offender: e.target.value }))} className={INPUT} placeholder="e.g. Roblox username" /></Field>
              <Field label="Platform"><input value={form.platform} onChange={e => setForm(f => ({ ...f, platform: e.target.value }))} className={INPUT} /></Field>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Original work"><input value={form.original_work} onChange={e => setForm(f => ({ ...f, original_work: e.target.value }))} className={INPUT} placeholder="What was copied?" /></Field>
              <Field label="Original URL"><input value={form.original_url} onChange={e => setForm(f => ({ ...f, original_url: e.target.value }))} className={INPUT} placeholder="https://starnightholdings.uk/…" /></Field>
            </div>
            <Field label="Describe the issue *" help="Please share enough detail for our team to investigate (min 20 chars).">
              <textarea required rows={5} minLength={20} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className={`${INPUT} resize-none`} data-testid="rep-description" />
            </Field>

            <div className="flex items-start gap-2 p-3 rounded-lg bg-white/[0.02] border border-white/[0.05] text-[11px] text-white/50 leading-relaxed">
              <Info className="w-3.5 h-3.5 text-[#00E5FF]/70 shrink-0 mt-0.5" />
              We only use your email to follow up on this report. Submitting knowingly false claims may have legal consequences.
            </div>

            <Button type="submit" disabled={submitting} data-testid="rep-submit"
              className="w-full h-12 rounded-full bg-gradient-to-br from-[#8A2BE2]/25 to-red-500/15 text-[#c39be5] border border-[#8A2BE2]/30 text-[11px] tracking-[0.15em] uppercase font-medium">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-2" />}
              Submit report
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

const INPUT = "w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2.5 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#8A2BE2]/30 transition-colors";
const Field = ({ label, children, help }) => (
  <div>
    <label className="text-[10px] tracking-[0.18em] uppercase text-white/50 font-medium block mb-1.5">{label}</label>
    {children}
    {help && <p className="text-[10px] text-white/30 mt-1">{help}</p>}
  </div>
);

export default ReportPiracyPage;
